import { ComponentProps, useState } from "react";
import { useNavigate } from "react-router-dom";

import PatientSearch from "components/patient-search";
import type { NewPatientSeed } from "components/patient-search/new-patient-seed.utils";
import PatientForm from "features/patients/patient-form";
import type { Patient } from "services/types/patients.type";

type PatientSearchWithQuickAddProps = Omit<
  ComponentProps<typeof PatientSearch>,
  "onAddNewPatient"
>;

/**
 * `PatientSearch` plus the New Patient Quick Add modal it opens when a search comes
 * back empty, seeded with the name / phone / email that was searched for.
 *
 * The modal lives here rather than inside `components/patient-search` on purpose: the
 * patient form's own referral-source field imports `PATIENTS_SEARCH_QUERY` back out of
 * the search component, so wiring it in there would close an import cycle.
 */
export default function PatientSearchWithQuickAdd({
  onPatientSelected,
  ...patientSearchProps
}: PatientSearchWithQuickAddProps) {
  const navigate = useNavigate();
  const [newPatientSeed, setNewPatientSeed] = useState<NewPatientSeed | null>(
    null
  );

  const handleSave = (patient: Patient) => {
    setNewPatientSeed(null);
    if (onPatientSelected) {
      onPatientSelected({ ...patient, group_key: "search" as const });
      return;
    }
    navigate(`/patients/${patient.id}/`);
  };

  return (
    <>
      <PatientSearch
        {...patientSearchProps}
        onPatientSelected={onPatientSelected}
        onAddNewPatient={setNewPatientSeed}
      />
      {/* Mounted only while open — the form's fields fetch on mount, and this search
          sits in the app's top bar on every page. */}
      {newPatientSeed && (
        <PatientForm
          isOpen
          newPatient={newPatientSeed}
          onCancel={() => setNewPatientSeed(null)}
          onSave={handleSave}
        />
      )}
    </>
  );
}
